import React, { useCallback } from 'react';
import { Alert, Button, Input, Space, theme, Tooltip, Typography } from 'antd';
import { AudioMutedOutlined, AudioOutlined, StopOutlined } from '@ant-design/icons';
import type { InputProps, InputRef, TextAreaProps } from 'antd/es/input';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import { useTranslation } from 'react-i18next';
import { useSpeechToText } from '@/hooks/useSpeechToText';
import type { SpeechRecognitionMode } from '@/utils/speechRecognition';

const { TextArea } = Input;
const { Text } = Typography;

type InputElement = React.ReactElement<InputProps & React.RefAttributes<InputRef>>;
type TextAreaElement = React.ReactElement<TextAreaProps & React.RefAttributes<TextAreaRef>>;
type TextInputElement = InputElement | TextAreaElement;
/** Ref exposed by the wrapper: the wrapped antd Input's or TextArea's imperative handle. */
type TextInputRef = InputRef | TextAreaRef;
type TextInputChangeEvent = React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;

export interface SpeechToTextWrapperProps {
  children: TextInputElement;
  /** When false, renders the child unchanged without speech controls. Default true. */
  enabled?: boolean;
  /**
   * `oneshot` — stop after a browser-detected pause.
   * `manual` — keep listening until the user clicks the mic again (default).
   */
  mode?: SpeechRecognitionMode;
  value?: string;
  onChange?: (e: TextInputChangeEvent) => void;
  id?: string;
  /**
   * Optional icon buttons rendered next to the mic in the TextArea footer
   * (or in the Input suffix). Placed to the left of the mic.
   */
  extraActions?: React.ReactNode;
  /** When true, the TextArea stretches to fill the available vertical space. */
  fillHeight?: boolean;
}

function isTextAreaElement(child: TextInputElement): child is TextAreaElement {
  return child.type === TextArea;
}

/**
 * Forwards a change event to the wrapped child's own `onChange`.
 * The event always originates from the child's element type (or is a synthetic value-only event).
 */
function forwardChildChange(child: TextInputElement, event: TextInputChangeEvent): void {
  if (isTextAreaElement(child)) {
    child.props.onChange?.(event as React.ChangeEvent<HTMLTextAreaElement, HTMLTextAreaElement>);
  } else {
    child.props.onChange?.(event as React.ChangeEvent<HTMLInputElement, HTMLInputElement>);
  }
}

function isSupportedTextInput(child: TextInputElement): boolean {
  if (child.type === Input || child.type === TextArea) return true;
  const displayName = (child.type as { displayName?: string })?.displayName;
  return displayName === 'Input' || displayName === 'TextArea';
}

function getStringValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value == null) return '';
  return String(value);
}

function formatTextAreaCount(
  showCount: TextAreaProps['showCount'],
  value: string,
  maxLength?: number,
): React.ReactNode {
  const count = value.length;
  if (typeof showCount === 'object' && showCount?.formatter) {
    return showCount.formatter({ value, count, maxLength });
  }
  return maxLength != null ? `${count} / ${maxLength}` : String(count);
}

/**
 * Optional wrapper for Ant Design `Input` or `Input.TextArea` that adds native
 * browser speech-to-text via the Web Speech API.
 *
 * Works as a direct `Form.Item` child — Ant Design injects `value` / `onChange`
 * onto this wrapper, which forwards them to the inner input.
 *
 * For TextArea, character count and action icons render in a footer below the
 * field (outside the textarea) to avoid overlapping typed text.
 *
 * Not supported: `Input.Password`, `Input.Search`, `InputNumber`.
 */
export const SpeechToTextWrapper = React.forwardRef<TextInputRef, SpeechToTextWrapperProps>(
  function SpeechToTextWrapper({ children, enabled = true, mode = 'manual', value, onChange, id, extraActions, fillHeight = false }, ref) {
    const { token } = theme.useToken();
    const { t } = useTranslation('common');

    // A callback ref fits both Input's and TextArea's ref types while forwarding to the wrapper's ref.
    const setInputRef = useCallback(
      (instance: TextInputRef | null) => {
        if (typeof ref === 'function') {
          ref(instance);
        } else if (ref) {
          ref.current = instance;
        }
      },
      [ref],
    );

    const stringValue = getStringValue(value ?? children.props.value);

    const handleValueChange = useCallback(
      (nextValue: string) => {
        if (onChange) {
          const syntheticEvent = {
            target: { value: nextValue },
            currentTarget: { value: nextValue },
          } as TextInputChangeEvent;
          onChange(syntheticEvent);
          return;
        }

        if (children.props.onChange) {
          const syntheticEvent = {
            target: { value: nextValue },
            currentTarget: { value: nextValue },
          } as TextInputChangeEvent;
          forwardChildChange(children, syntheticEvent);
        }
      },
      [children, onChange],
    );

    const {
      speechSupported,
      isRecording,
      sttUiState,
      sttError,
      toggle,
      clearError,
    } = useSpeechToText({
      value: stringValue,
      onChange: handleValueChange,
      enabled,
      mode,
    });

    const isTextArea = isTextAreaElement(children);
    const isSupported = isSupportedTextInput(children);

    if (!enabled || !isSupported) {
      if (import.meta.env.DEV && enabled && !isSupported) {
        console.warn('[SpeechToTextWrapper] Child must be Input or Input.TextArea.');
      }
      return isTextAreaElement(children)
        ? React.cloneElement(children, { ref: setInputRef, value, onChange, id })
        : React.cloneElement(children, { ref: setInputRef, value, onChange, id });
    }

    const isListening = sttUiState === 'listening';

    const micTitle = !speechSupported
      ? t('speechToText.unsupported')
      : isRecording
        ? t('speechToText.stopVoiceInput')
        : t('speechToText.voiceInput');

    const micButton = (
      <Tooltip title={micTitle}>
        <Button
          type="text"
          size="small"
          aria-label={micTitle}
          disabled={!speechSupported}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggle();
          }}
          icon={
            !speechSupported ? (
              <AudioMutedOutlined />
            ) : isRecording ? (
              <StopOutlined />
            ) : (
              <AudioOutlined />
            )
          }
          style={{
            color: isRecording ? token.colorPrimary : token.colorTextSecondary,
            animation: isRecording ? 'speechToTextPulse 1.2s ease-in-out infinite' : undefined,
          }}
        />
      </Tooltip>
    );

    const handleInputChange = (e: TextInputChangeEvent) => {
      clearError();
      onChange?.(e);
      forwardChildChange(children, e);
    };

    const textAreaProps = isTextAreaElement(children) ? children.props : undefined;
    const showFooterCount = Boolean(isTextArea && textAreaProps?.showCount);
    const showFooterActions = Boolean(isTextArea && (speechSupported || extraActions));
    const showFooter = showFooterCount || showFooterActions;
    const maxLength = textAreaProps?.maxLength;

    // Props shared by Input and TextArea; the child's own props are kept by cloneElement.
    const mergedProps: InputProps & TextAreaProps = {
      id: id ?? children.props.id,
      onChange: handleInputChange,
      readOnly: isListening || children.props.readOnly,
      placeholder: children.props.placeholder,
      value: value ?? children.props.value,
      style: {
        ...children.props.style,
        ...(isListening
          ? {
              borderColor: token.colorPrimary,
              boxShadow: `0 0 0 2px ${token.colorPrimaryBg}`,
            }
          : {}),
      },
    };

    if (isTextArea) {
      const textAreaStyles = textAreaProps?.styles;
      const existingTextareaStyle =
        typeof textAreaStyles === 'object' && textAreaStyles && 'textarea' in textAreaStyles
          ? textAreaStyles.textarea
          : undefined;

      // Render count in our footer instead of Ant Design's in-field counter.
      mergedProps.showCount = false;
      mergedProps.styles = {
        ...(typeof textAreaStyles === 'object' ? textAreaStyles : {}),
        textarea: {
          ...(typeof existingTextareaStyle === 'object' ? existingTextareaStyle : {}),
          ...(fillHeight
            ? {
                height: '100%',
                minHeight: 0,
                resize: 'none',
                overflow: 'auto',
              }
            : {}),
        },
      };

      if (fillHeight) {
        mergedProps.className = [children.props.className, 'speech-to-text-fill-height', 'speech-to-text-textarea']
          .filter(Boolean)
          .join(' ');
        mergedProps.style = {
          ...mergedProps.style,
          height: '100%',
        };
      } else {
        mergedProps.className = [children.props.className, 'speech-to-text-textarea'].filter(Boolean).join(' ');
      }
    }

    const footerActions = showFooterActions ? (
      <Space size={0}>
        {extraActions}
        {speechSupported ? micButton : null}
      </Space>
    ) : null;

    const clonedInput = isTextAreaElement(children) ? (
      React.cloneElement(children, { ...mergedProps, ref: setInputRef })
    ) : (
      React.cloneElement(children, {
        ...mergedProps,
        ref: setInputRef,
        suffix: children.props.suffix || extraActions || speechSupported ? (
          <Space size={4}>
            {children.props.suffix}
            {extraActions}
            {speechSupported ? micButton : null}
          </Space>
        ) : (
          children.props.suffix
        ),
      })
    );

    const errorMessage =
      sttError === 'permission_denied'
        ? t('speechToText.permissionDenied')
        : sttError === 'no_speech'
          ? t('speechToText.noSpeech')
          : null;

    const listeningBanner = isListening ? (
      <div
        role="status"
        aria-live="polite"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: token.marginXS,
          marginBottom: token.marginXXS,
          color: token.colorPrimary,
          fontSize: token.fontSizeSM,
        }}
      >
        <AudioOutlined />
        <span>{t('speechToText.listening')}</span>
      </div>
    ) : null;

    const footer = showFooter ? (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: token.marginXS,
          marginTop: token.marginXXS,
          flexShrink: 0,
          minHeight: token.controlHeightSM,
        }}
      >
        {showFooterCount ? (
          <Text type="secondary" style={{ fontSize: token.fontSizeSM, lineHeight: `${token.controlHeightSM}px` }}>
            {formatTextAreaCount(textAreaProps?.showCount, stringValue, maxLength)}
          </Text>
        ) : null}
        {footerActions}
      </div>
    ) : null;

    const content = (
      <div
        className={fillHeight ? 'speech-to-text-fill-container' : undefined}
        style={
          fillHeight
            ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
            : undefined
        }
      >
        <div
          style={
            fillHeight
              ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
              : undefined
          }
        >
          {clonedInput}
        </div>
        {footer}
      </div>
    );

    return (
      <div
        className={fillHeight ? 'speech-to-text-fill-root' : undefined}
        style={{
          width: '100%',
          ...(fillHeight ? { height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 } : {}),
        }}
      >
        <style>
          {`
            @keyframes speechToTextPulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.45; }
            }
            .speech-to-text-fill-container .speech-to-text-fill-height.ant-input-textarea,
            .speech-to-text-fill-container .speech-to-text-fill-height.ant-input-textarea-affix-wrapper {
              flex: 1;
              min-height: 0;
              display: flex;
              flex-direction: column;
            }
            .speech-to-text-fill-container .speech-to-text-fill-height textarea {
              flex: 1;
              min-height: 0;
            }
            .speech-to-text-fill-height textarea,
            .speech-to-text-textarea textarea {
              scrollbar-width: thin;
              scrollbar-color: ${token.colorTextQuaternary} transparent;
            }
            .speech-to-text-fill-height textarea::-webkit-scrollbar,
            .speech-to-text-textarea textarea::-webkit-scrollbar {
              width: 6px;
            }
            .speech-to-text-fill-height textarea::-webkit-scrollbar-track,
            .speech-to-text-textarea textarea::-webkit-scrollbar-track {
              background: transparent;
            }
            .speech-to-text-fill-height textarea::-webkit-scrollbar-thumb,
            .speech-to-text-textarea textarea::-webkit-scrollbar-thumb {
              background-color: ${token.colorTextQuaternary};
              border-radius: 999px;
            }
            .speech-to-text-fill-height textarea::-webkit-scrollbar-thumb:hover,
            .speech-to-text-textarea textarea::-webkit-scrollbar-thumb:hover {
              background-color: ${token.colorTextTertiary};
            }
          `}
        </style>
        {listeningBanner}
        {content}
        {errorMessage ? (
          <Alert
            type="warning"
            showIcon
            title={errorMessage}
            style={{ marginTop: token.marginXXS }}
          />
        ) : null}
      </div>
    );
  },
);

SpeechToTextWrapper.displayName = 'SpeechToTextWrapper';

export default SpeechToTextWrapper;
